<template>
  <div class="home">
    <!-- Continuous Scrolling Gallery -->
    <ScrollingGallery 
      :images="allImages" 
      :show-labels-always="true"
      @image-click="openLightbox"
      @image-hover="preloadImage"
    />

    <!-- Overlay Content -->
    <div class="gallery-overlay">
      <div class="overlay-content">
        <h1>
          <span class="brand-name">
            <span class="large-cap">P</span>OSE 
            <span class="large-cap">P</span>ERFECT
          </span>
          <span class="brand-type">Photography</span>
        </h1>
      </div>
    </div>
    
    <!-- Lightbox -->
    <div v-if="lightboxOpen" class="lightbox" @click="closeLightbox">
      <div class="lightbox-content" @click.stop>
        <button class="lightbox-close" @click="closeLightbox">&times;</button>
        <div class="lightbox-image">
          <template v-if="selectedImage">
            <!-- Thumbnail (already cached from the feed) shown instantly, blurred,
                 so the modal is never blank while the sharp image streams in. -->
            <img
              :src="thumbSrc(selectedImage)"
              :alt="selectedImage.description"
              class="lightbox-img lightbox-thumb"
              :class="{ hidden: sharpLoaded }"
              aria-hidden="true"
            />
            <!-- Sharp, responsive image: the browser picks a variant sized for
                 the modal (not the largest WebP). Fades in once decoded. -->
            <picture>
              <source
                v-for="s in lightboxPicture(selectedImage).sources"
                :key="s.type"
                :type="s.type"
                :srcset="s.srcset"
                sizes="90vw"
              />
              <img
                :src="lightboxPicture(selectedImage).jpgSrc || fullSrc(selectedImage)"
                sizes="90vw"
                :alt="selectedImage.description"
                class="lightbox-img lightbox-sharp"
                :class="{ visible: sharpLoaded }"
                decoding="async"
                fetchpriority="high"
                @load="onSharpLoad"
              />
            </picture>
          </template>
        </div>
        <div class="lightbox-info">
          <h3>{{ selectedImage?.title }}</h3>
          <p>{{ selectedImage?.description }}</p>
          <RouterLink 
            v-if="selectedImage?.portfolioLink"
            :to="selectedImage.portfolioLink" 
            class="portfolio-link"
            @click="closeLightbox"
          >
            View {{ selectedImage.title }} Portfolio →
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import ScrollingGallery from '@/components/ScrollingGallery.vue'
import { imageService, type GalleryImage } from '@/services/imageService'
import { portfolioService, pictureSourcesFor, buildSrcsetFor, type FeedImage, type PictureModel } from '@/services/portfolioService'
import { settingsService } from '@/services/settingsService'

const lightboxOpen = ref(false)
const selectedImage = ref<any | null>(null)
// Whether the sharp (full) image has decoded yet. Until then we show the
// already-cached thumbnail so the modal is never blank.
const sharpLoaded = ref(false)

// Primary source: the API-backed portfolio feed (curated `home` surface, responsive).
// Fallback: the legacy static list, filtered by enabled categories — so the homepage
// still renders if the API is briefly unreachable or the feed is empty.
const allImages = ref<any[]>([])

const staticFallback = (): GalleryImage[] =>
  imageService.getGalleryImages().filter(image => settingsService.isCategoryEnabled(image.category))

async function loadFeed() {
  try {
    const feed: FeedImage[] = await portfolioService.getFeed('home')
    allImages.value = feed.length ? feed : staticFallback()
  } catch {
    allImages.value = staticFallback()
  }
}

const fullSrc = (image: any): string => {
  // Feed image: use its largest src. Legacy: resolve via imageService.
  if (image?.src) return image.src
  return image ? imageService.getFullSizeUrl(image as GalleryImage) : ''
}

// The smallest variant for an image — the one the FEED already downloaded and
// cached. Shown instantly as the lightbox's first paint (blurred-up), so the
// modal is never blank while the sharp version streams in.
const thumbSrc = (image: any): string => {
  const vs = image?.variants as { url: string; width: number; format: string }[] | undefined
  if (vs?.length) {
    // Prefer the smallest webp/jpg (what the grid loaded at 30–45vw).
    const small = [...vs]
      .filter(v => (v.format === 'webp' || v.format === 'jpg') && v.width)
      .sort((a, b) => a.width - b.width)[0]
    if (small) return small.url
  }
  return fullSrc(image)
}

// A responsive <picture> for the lightbox: AVIF → WebP → JPG srcset so the
// browser picks a variant sized for the MODAL (≈90vw, capped at the full
// width), not the single largest WebP. This is the core speed win — a laptop
// pulls the ~1400/2150 variant and a phone the ~600/900, instead of a 2160/4k.
const lightboxPicture = (image: any): PictureModel => {
  const model = pictureSourcesFor(image?.variants, fullSrc(image))
  // pictureSourcesFor already orders AVIF→WebP and gives a jpg/webp fallback.
  return model
}

// Preload the sharp image for a given item (fired on hover/pointerdown), so by
// the time the click lands the bytes are often already in cache.
const preloadedTokens = new Set<string>()
const preloadImage = (image: any) => {
  const key = image?.token || image?.id || fullSrc(image)
  if (!key || preloadedTokens.has(key)) return
  preloadedTokens.add(key)
  const img = new Image()
  // Use the same srcset the modal will use so the preloaded bytes are reused.
  const webp = buildSrcsetFor(image?.variants || [], 'webp')
  if (webp) { img.srcset = webp; img.sizes = '90vw' }
  img.src = fullSrc(image)
}

const openLightbox = (image: any, _columnIndex: number, _imageIndex: number) => {
  selectedImage.value = image
  sharpLoaded.value = false
  lightboxOpen.value = true
  document.body.style.overflow = 'hidden'
}

const onSharpLoad = () => { sharpLoaded.value = true }

const closeLightbox = () => {
  lightboxOpen.value = false
  selectedImage.value = null
  sharpLoaded.value = false
  document.body.style.overflow = 'auto'
}

const handleKeydown = (e: KeyboardEvent) => {
  if (!lightboxOpen.value) return
  if (e.key === 'Escape') closeLightbox()
}

onMounted(() => {
  loadFeed()
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = 'auto'
})
</script>

<style scoped>
.home {
  width: 100%;
  height: 100vh;
  height: 100dvh; /* dynamic vh: excludes mobile browser chrome, so no vertical scroll under the address bar */
  overflow: hidden;
}

/* Gallery Controls */
.gallery-controls {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.label-toggle {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-variant: small-caps;
  transition: all 0.3s ease;
}

.label-toggle:hover {
  background: rgba(0, 0, 0, 0.9);
  border-color: rgba(255, 255, 255, 0.4);
}

.label-toggle.active {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

/* Scrolling Gallery */
.scrolling-gallery {
  height: 100vh;
  height: 100dvh;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  background: #000000;
}

.gallery-columns {
  display: flex;
  height: 100%;
  gap: 1rem;
  padding: 0 2rem;
  margin: 0;
}

.gallery-column {
  flex: 1;
  height: 100%;
  overflow: hidden;
}

.column-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  animation: scrollUp 60s linear infinite;
}

@keyframes scrollUp {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
}

.gallery-item {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.gallery-item:hover {
  transform: scale(1.02);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.image-container {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 2rem 1rem 1rem;
  transform: translateY(0);
  transition: all 0.3s ease;
}

.image-overlay.hover-only {
  transform: translateY(100%);
}

.gallery-item:hover .image-overlay.hover-only {
  transform: translateY(0);
}

.image-label {
  color: white;
  font-size: 1rem;
  font-weight: 500;
  font-variant: small-caps;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.gallery-item:hover .image-overlay {
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
}

.gallery-item:hover .image-label {
  font-weight: 600;
  text-shadow: 0 3px 6px rgba(0, 0, 0, 0.7);
}

/* Gallery Overlay */
.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
}

.overlay-content {
  text-align: center;
  color: white;
  max-width: 100vw;
  padding: 2rem;
  pointer-events: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  /* width: 100%; */
}

.overlay-content h1 {
  /* Fluid: scales with viewport width so it never overflows a phone, and
     never balloons past 7rem on desktop. The vw factor is deliberately modest
     (9vw) so "PERFECT" on one line stays inside a narrow phone; min keeps it
     readable on tiny screens. */
  font-size: clamp(1.75rem, 9vw, 7rem);
  font-weight: 300;
  margin-bottom: 0;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.8);
  letter-spacing: clamp(1px, 0.5vw, 4px);
  font-family: 'Georgia', 'Times New Roman', serif;
  color: #ffffff;
  position: relative;
  text-align: center;
  line-height: 1.1;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.brand-name {
  display: block;
  margin-bottom: 0.2em;
  white-space: nowrap;
  max-width: 100%;
  overflow-wrap: break-word;
  font-family: "eloquent-jf-pro", 'Playfair Display', 'Times New Roman', serif;
  text-transform: uppercase;
}

.large-cap {
  font-size: 1.2em;
  font-weight: 400;
}

.brand-type {
  display: block;
  font-size: 0.8em;
  margin-left: 4em;
  opacity: 0.9;
  font-family: "eloquent-jf-pro", 'Playfair Display', 'Times New Roman', serif;
  font-style: italic;
  white-space: nowrap;
}

.overlay-content h1::after {
  content: '';
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #ffffff, transparent);
  opacity: 0.4;
}

.overlay-content p {
  font-size: 1.4rem;
  margin-bottom: 0;
  opacity: 0.85;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.6);
  font-weight: 300;
  letter-spacing: 0.5px;
  font-style: italic;
}

.overlay-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-block;
  padding: 1rem 2rem;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  text-align: center;
  border: 2px solid transparent;
}

.btn-primary {
  background: white;
  color: #333;
}

.btn-primary:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
  background: transparent;
  color: white;
  border-color: white;
}

.btn-secondary:hover {
  background: white;
  color: #333;
}

/* Lightbox */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lightbox-image {
  max-width: 80vw;
  max-height: 70vh;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.lightbox-img {
  max-width: 80vw;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
}

/* Blur-up: the cached thumbnail sits under the sharp image, scaled up and
   softened. It stays visible until the sharp image decodes, then fades out —
   so the modal shows SOMETHING immediately instead of a blank frame. */
.lightbox-thumb {
  filter: blur(12px);
  transform: scale(1.02); /* hide the blurred edge bleed */
  transition: opacity 0.3s ease;
}
.lightbox-thumb.hidden { opacity: 0; }

/* The sharp image fades in over the thumbnail once it has loaded. Absolutely
   positioned so it overlays the thumb (same box) rather than stacking below. */
.lightbox-sharp {
  position: absolute;
  inset: 0;
  margin: auto;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.lightbox-sharp.visible { opacity: 1; }

.lightbox-image .image-placeholder {
  background: linear-gradient(135deg, #333333 0%, #1a1a1a 100%);
  color: white;
  font-size: 1.2rem;
  min-height: 400px;
  border-radius: 8px;
  border: 1px solid #444;
}

.lightbox-close {
  position: absolute;
  top: -50px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 2001;
}

/* On phones the content hugs the top, so a -50px offset puts the ✕ off-screen.
   Pin it to the viewport's top-right (inside the safe area) with a tap-friendly
   hit target and a subtle backing so it reads against any image. */
@media (max-width: 768px) {
  .lightbox-close {
    position: fixed;
    top: calc(env(safe-area-inset-top, 0px) + 10px);
    right: calc(env(safe-area-inset-right, 0px) + 10px);
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
  }
}

.lightbox-info {
  text-align: center;
  color: white;
  max-width: 600px;
}

.lightbox-info h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.lightbox-info p {
  opacity: 0.8;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.portfolio-link {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.portfolio-link:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

/* Footer */
.minimal-footer {
  background: white;
  border-top: 1px solid #eee;
  padding: 2rem 0;
}

.footer-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #666;
  font-size: 0.9rem;
}

.footer-links {
  display: flex;
  gap: 2rem;
}

.footer-links a {
  color: #666;
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-links a:hover {
  color: #333;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Responsive Design — font-size/letter-spacing are fluid via clamp() on
   .overlay-content h1, so these only handle layout: gallery padding, the
   brand-type offset, and allowing the brand name to wrap on tiny screens. */
@media (max-width: 768px) {
  .scrolling-gallery {
    height: 100vh;
    height: 100dvh;
  }
  
  .gallery-columns {
    padding: 0 1rem;
    gap: 0.5rem;
  }
  
  .brand-type {
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .overlay-content {
    padding: 1rem;
    max-width: 100vw;
  }

  /* Let the brand name wrap instead of forcing one over-wide line
     (the real cause of the horizontal scroll on phones). */
  .brand-name {
    white-space: normal;
  }
}

@media (max-width: 360px) {
  .brand-type {
    margin-left: 0;
  }
}

/* Pause animation on hover */
.gallery-column:hover .column-content {
  animation-play-state: paused;
}
</style>
